import React, { useState, useEffect } from 'react';
import { Icon } from 'ui';
import { routes } from 'constants/config';
import addModifiers from 'utils/add_modifiers';
import request from 'utils/request_ajax';
import { sort } from 'utils/sort';
import CriteriaSearch from './CriteriaSearch';
import CriteriaTags from './CriteriaTags';
import CriteriaList from './CriteriaList';

const AddCriteriaBody = ({
  selectedCriteria,
  canUseCustomFeedbackCriteria,
  notificationText,
  setCriterionNotification,
  currentUserId,
  toggleModalState,
  handleUpdate,
  toggleModal,
}) => {
  const [criteria, setCriteria] = useState([]);
  const [tags, setTags] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [selectedCriteriaList, setSelectedCriteriaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const editing = !!selectedCriteria;
  const disabled =
    (!editing && selectedCriteriaList.length === 0) || loading || error;

  const checkCustomCriteria = criteriaArg => {
    return criteriaArg.some(
      criterion => criterion.type === 'FeedbackCriteria::CustomCriterion',
    );
  };

  const addCustomTag = addedTags => {
    const customTag = {
      id: 'FeedbackCriteria::CustomCriterion',
      name: 'Custom',
    };
    return [customTag, ...addedTags];
  };

  useEffect(() => {
    const getData = () => {
      const selectedCriteriaIds = selectedCriteria
        ? selectedCriteria.map(selectedCriterion => selectedCriterion.id)
        : [];

      request
        .get(routes.FEEDBACK_CRITERIA.CRITERIA)
        .then(payload => {
          const sortedCriteria = sort(payload.data.criteria, 'name');
          const hasCustomCriteria =
            canUseCustomFeedbackCriteria &&
            checkCustomCriteria(payload.data.criteria);
          const newTags = hasCustomCriteria
            ? addCustomTag(payload.data.tags)
            : payload.data.tags;
          const newSelectedCriteria = sortedCriteria.filter(criterion =>
            selectedCriteriaIds.includes(criterion.id),
          );
          setCriteria(sortedCriteria);
          setTags(newTags);
          setSelectedCriteriaList(newSelectedCriteria);
          setLoading(false);
        })
        .fail(() => {
          setError(t('feedback_criteria.error.no_connection'));
          setLoading(false);
        });
    };
    getData();
  }, [canUseCustomFeedbackCriteria, selectedCriteria]);

  const toggleQuery = queryToSet => {
    setQuery(queryToSet);
  };

  const toggleTag = tagIdToSet => {
    setSelectedTagId(tagIdToSet);
  };

  const selectCriterion = criterion => {
    setSelectedCriteriaList([criterion, ...selectedCriteriaList]);
  };

  const deselectCriterion = criterion => {
    const criteriaSelected = selectedCriteriaList.filter(
      selectedCriterion => selectedCriterion.id !== criterion.id,
    );
    setSelectedCriteriaList(criteriaSelected || []);
  };

  const updateCriteria = () => {
    handleUpdate(selectedCriteriaList);
    toggleModal();
  };

  return (
    <div>
      <div className="ModalTakeover-title">
        {t('feedback_criteria.add_criteria')}
      </div>
      <CriteriaSearch
        searchFeedbackCriteria={t('feedback_criteria.search')}
        toggleQuery={toggleQuery}
        query={query}
      />
      {tags.length > 0 && (
        <CriteriaTags
          selectedTagId={selectedTagId}
          tags={tags}
          toggleTag={toggleTag}
          allCriteria={t('feedback_criteria.all_criteria')}
        />
      )}
      {notificationText && (
        <div className="NotificationContainer NotificationContainer--secondary NotificationContainer--success bdrs-2 mb-16 d-f ai-c jc-sb">
          {notificationText}
          <Icon
            name="error"
            className="c-p mt-0 ml-16 fz-bigger"
            onClick={() => setCriterionNotification('')}
          />
        </div>
      )}
      <CriteriaList
        criteria={criteria}
        selectedTagId={selectedTagId}
        query={query}
        selectedCriteria={selectedCriteriaList}
        selectCriterion={selectCriterion}
        deselectCriterion={deselectCriterion}
        loading={loading}
        error={error}
        toggleModalState={toggleModalState}
        canUseCustomFeedbackCriteria={canUseCustomFeedbackCriteria}
        currentUserId={currentUserId}
      />
      <div className="d-f ai-c">
        {canUseCustomFeedbackCriteria && (
          <div
            className="AddCriteriaModal-createNewButton c-brand-blue c-p h-td-u fz-bigger"
            onClick={() => toggleModalState(null)}
            onKeyDown={e => e.key === 'Enter' && toggleModalState(null)}
            tabIndex="0"
            role="button"
          >
            <Icon name="add" />
            <span className="ml-4">
              {t('feedback_criteria.criterion.create_new')}
            </span>
          </div>
        )}
        <button
          type="button"
          onClick={updateCriteria}
          className={`ml-a ${addModifiers('NewButton', [
            'tallAndWide',
            'blue',
          ])}`}
          disabled={disabled}
        >
          {loading ? t('common.loading') : t('common.done')}
        </button>
      </div>
    </div>
  );
};

export default AddCriteriaBody;
